import React, { useState, useEffect, useMemo } from 'react';
import {
  FiPieChart,
  FiFilter,
  FiCheckCircle,
  FiUsers,
  FiMapPin,
  FiClock,
  FiSearch,
  FiLock
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../../Api';
import { usePermission } from '../../../hooks/usePermission';
import {
  PageComponent,
  Card,
  MultiSelect,
  DatePeriodFilter,
  getDateParams,
  Skeletons,
  Input,
  Select,
} from '../../../components';
import '../../CompanyManager/style.css';

const INSPECTION_TYPES = {
  employee: 'Funcionário',
  free: 'Livre',
  station: 'Posto',
  vehicle: 'Veículo',
  infrastructure: 'Infraestrutura',
  others: 'Outros',
};

const EXECUTOR_TYPES = {
  all: 'Todos',
  employee: 'Funcionário',
  user: 'Usuário',
  customer: 'Cliente',
  supplier: 'Fornecedor',
  accountant: 'Contador',
};

export default function InspectionOverview() {
  const permissions = usePermission('inspections');
  const [companies, setCompanies] = useState([]);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);

  const [kpis, setKpis] = useState({
    totalExecutions: 0,
    uniqueTemplates: 0,
    uniqueExecutors: 0,
    withGeolocation: 0,
    avgPerDay: 0,
  });
  const [topInspections, setTopInspections] = useState([]);
  const [topExecutors, setTopExecutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [periodMode, setPeriodMode] = useState('daily');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });

  const [typeFilter, setTypeFilter] = useState('all');
  const [executorTypeFilter, setExecutorTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const datePeriod = useMemo(
    () => getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate),
    [periodMode, currentDate, customPeriod]
  );

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    (async () => {
      try {
        const data = await Api.getCompanies();
        const list = Array.isArray(data) ? data : (data.data || []);
        setCompanies(list);
        setCompanyFilterValues(['all']);
      } catch (error) {
        toast.error('Erro ao carregar empresas.');
      }
    })();
  }, []);

  useEffect(() => {
    if (companies.length === 0) return;
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, companyFilterValues, periodMode, currentDate, typeFilter, executorTypeFilter, debouncedSearchTerm, permissions.canView]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);
  }

  async function loadOverview() {
    if (!permissions.canView) return;
    if (companies.length === 0) return;

    setLoading(true);
    try {
      const allValue = 'all';
      const specificIds = companyFilterValues.filter(v => v !== allValue);
      const hasAllSelected = companyFilterValues.includes(allValue);

      const ids = hasAllSelected || specificIds.length === 0
        ? companies.map(c => c.id)
        : specificIds.map(id => Number(id));

      if (ids.length === 0) {
        setKpis({
            totalExecutions: 0,
            uniqueTemplates: 0,
            uniqueExecutors: 0,
            withGeolocation: 0,
            avgPerDay: 0,
        });
        setTopInspections([]);
        setTopExecutors([]);
        setHasLoadedOnce(true);
        return;
      }

      const params = {
          companyIds: ids.join(','),
      };

      if (datePeriod.startDate) params.startDate = datePeriod.startDate;
      if (datePeriod.endDate) params.endDate = datePeriod.endDate;
      if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
      if (executorTypeFilter && executorTypeFilter !== 'all') params.executorType = executorTypeFilter;
      if (debouncedSearchTerm) params.searchTerm = debouncedSearchTerm;

      const data = await Api.getInspectionOverview(params);

      if (data) {
          setKpis(data.kpis || {
            totalExecutions: 0,
            uniqueTemplates: 0,
            uniqueExecutors: 0,
            withGeolocation: 0,
            avgPerDay: 0,
          });
          setTopInspections(data.topInspections || []);
          setTopExecutors(data.topExecutors || []);
      }

      setHasLoadedOnce(true);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dados de inspeções.');
    } finally {
      setLoading(false);
    }
  }

  // Removed client-side filteredExecutions, kpis, topInspections, topExecutors calculation logic

  const typeOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      ...Object.entries(INSPECTION_TYPES).map(([value, label]) => ({ value, label })),
    ],
    []
  );

  const executorTypeOptions = useMemo(
    () =>
      Object.entries(EXECUTOR_TYPES).map(([value, label]) => ({
        value,
        label,
      })),
    []
  );

  const periodLabel = useMemo(() => {
    const { startDate, endDate } = datePeriod;
    if (!startDate && !endDate) return 'Todos os períodos';
    if (startDate && !endDate) return `A partir de ${new Date(startDate).toLocaleDateString('pt-BR')}`;
    if (!startDate && endDate) return `Até ${new Date(endDate).toLocaleDateString('pt-BR')}`;
    return `${new Date(startDate).toLocaleDateString('pt-BR')} até ${new Date(endDate).toLocaleDateString('pt-BR')}`;
  }, [datePeriod]);

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar este módulo.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        <div className="manager-header">
          <div className="manager-header-title">
            <div className="manager-header-icon">
              <FiPieChart size={22} />
            </div>
            <div>
              <h1>Visão Geral - Inspeções</h1>
              <p>Dashboard com indicadores e resumos das inspeções executadas.</p>
            </div>
          </div>
        </div>

        {loading && !hasLoadedOnce && (
          <Skeletons.CompanyManagerSkeleton />
        )}

        {(!loading || hasLoadedOnce) && (
          <>
            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
              </div>

              <div
                className="manager-filters-grid"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}
              >
                <div className="manager-filter-group">
                  <DatePeriodFilter
                    periodMode={periodMode}
                    setPeriodMode={setPeriodMode}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    customRange={customPeriod}
                    setCustomRange={setCustomPeriod}
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="inspection-overview-company-filter"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({
                        value: String(c.id),
                        label:
                          c.corporate_name ||
                          c.trade_name ||
                          c.name ||
                          c.razao_social ||
                          c.nome_fantasia ||
                          `Empresa ${c.id}`,
                      })),
                    ]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Tipo de Inspeção</label>
                  <Select
                    id="inspection-type-filter"
                    options={typeOptions}
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Tipo de Executor</label>
                  <Select
                    id="inspection-executor-type-filter"
                    options={executorTypeOptions}
                    value={executorTypeFilter}
                    onChange={e => setExecutorTypeFilter(e.target.value)}
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Busca</label>
                  <Input
                    id="inspection-overview-search"
                    icon={FiSearch}
                    placeholder="Buscar por inspeção ou executor..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </Card>

            <Card style={{ marginTop: '1rem' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '1rem',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '0.75rem',
                      backgroundColor: '#eff6ff',
                      color: '#1d4ed8',
                    }}
                  >
                    <FiCheckCircle size={18} />
                  </div>
                  <div>
                    <div className="manager-table-title">Inspeções Executadas</div>
                    <div className="manager-table-subtitle">{kpis.totalExecutions.toLocaleString('pt-BR')}</div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '0.75rem',
                      backgroundColor: '#ecfdf5',
                      color: '#047857',
                    }}
                  >
                    <FiPieChart size={18} />
                  </div>
                  <div>
                    <div className="manager-table-title">Modelos Diferentes</div>
                    <div className="manager-table-subtitle">{kpis.uniqueTemplates.toLocaleString('pt-BR')}</div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '0.75rem',
                      backgroundColor: '#fefce8',
                      color: '#92400e',
                    }}
                  >
                    <FiUsers size={18} />
                  </div>
                  <div>
                    <div className="manager-table-title">Executores Únicos</div>
                    <div className="manager-table-subtitle">{kpis.uniqueExecutors.toLocaleString('pt-BR')}</div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '0.75rem',
                      backgroundColor: '#f5f3ff',
                      color: '#6d28d9',
                    }}
                  >
                    <FiMapPin size={18} />
                  </div>
                  <div>
                    <div className="manager-table-title">Com Geolocalização</div>
                    <div className="manager-table-subtitle">{kpis.withGeolocation.toLocaleString('pt-BR')}</div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '0.75rem',
                      backgroundColor: '#eff6ff',
                      color: '#1f2937',
                    }}
                  >
                    <FiClock size={18} />
                  </div>
                  <div>
                    <div className="manager-table-title">Média por dia</div>
                    <div className="manager-table-subtitle">
                      {kpis.avgPerDay.toLocaleString('pt-BR')} inspeções/dia
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="manager-table-subtitle"
                style={{ marginTop: '0.75rem', color: '#6b7280' }}
              >
                Período analisado: {periodLabel}
              </div>
            </Card>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '1rem',
                marginTop: '1rem',
              }}
            >
              <Card>
                <h3 className="section-title" style={{ marginBottom: '0.75rem', marginTop: '0px' }}>
                  Top inspeções no período
                </h3>
                {topInspections.length === 0 ? (
                  <p className="manager-table-subtitle">Nenhuma inspeção encontrada com os filtros atuais.</p>
                ) : (
                  <ul
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      maxHeight: 220,
                      overflowY: 'auto',
                    }}
                  >
                    {topInspections.map((item, index) => (
                      <li
                        key={`${item.name}-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.5rem 0',
                          borderBottom: index === topInspections.length - 1 ? 'none' : '1px solid #e5e7eb',
                        }}
                      >
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: '999px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '0.75rem',
                            backgroundColor: item.color || '#3B82F6',
                            color: '#fff',
                            fontSize: 12,
                          }}
                        >
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="manager-table-title">{item.name}</div>
                          <div className="manager-table-subtitle">
                            {INSPECTION_TYPES[item.type] || 'Outros'} • {item.count.toLocaleString('pt-BR')}{' '}
                            execuções
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card>
                <h3 className="section-title" style={{ marginBottom: '0.75rem', marginTop: '0px' }}>
                  Principais executores
                </h3>
                {topExecutors.length === 0 ? (
                  <p className="manager-table-subtitle">Nenhum executor encontrado com os filtros atuais.</p>
                ) : (
                  <ul
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      maxHeight: 220,
                      overflowY: 'auto',
                    }}
                  >
                    {topExecutors.map((item, index) => (
                      <li
                        key={`${item.name}-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.5rem 0',
                          borderBottom: index === topExecutors.length - 1 ? 'none' : '1px solid #e5e7eb',
                        }}
                      >
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: '999px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '0.75rem',
                            backgroundColor: '#0f766e',
                            color: '#fff',
                            fontSize: 12,
                          }}
                        >
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="manager-table-title">{item.name}</div>
                          <div className="manager-table-subtitle">
                            {item.count.toLocaleString('pt-BR')} inspeções executadas
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </PageComponent>
  );
}
